// API utility functions
import axios from "axios";
import { getAccessToken } from "../../utils/authStorage";

const API_BASE_URL = (
  import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1"
)
  .trim()
  .replace(/\/$/, "");

// Game API functions
export const gameAPI = {
  // Get all games
  getGames: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/games?${query}`);
  },

  // Get single game
  getGameById: (id) => apiCall(`/games/${id}`),

  // Get available vouchers
  getAvailableVouchers: () => apiCall("/games/vouchers/available"),

  // Validate game user
  validateGameUser: (data) =>
    apiCall("/games/verify-userid", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }),

  // Admin operations
  admin: {
    // Create game
    createGame: (gameData) =>
      apiCall("/games", {
        method: "POST",
        body: JSON.stringify(gameData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Update game
    updateGame: (id, gameData) =>
      apiCall(`/games/${id}`, {
        method: "PUT",
        body: JSON.stringify(gameData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Delete game
    deleteGame: (id) =>
      apiCall(`/games/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Manage packs
    addPack: (gameId, packData) =>
      apiCall(`/games/${gameId}/packs`, {
        method: "POST",
        body: JSON.stringify(packData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    updatePack: (gameId, packId, packData) =>
      apiCall(`/games/${gameId}/packs/${packId}`, {
        method: "PUT",
        body: JSON.stringify(packData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    deletePack: (gameId, packId) =>
      apiCall(`/games/${gameId}/packs/${packId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Provider APIs
    getProviderGames: (provider) =>
      apiCall(`/games/api-games/${provider}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    getProviderServers: (provider, gameId) =>
      apiCall(`/games/api-servers/${provider}/${gameId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    getProviderPacks: (provider, gameId) =>
      apiCall(`/games/api-packs/${provider}/${gameId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),
  },
};

// Generic API call handler
const apiCall = async (url, options = {}) => {
  const { headers, ...rest } = options;

  // Ensure url starts with / and remove redundant slashes
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  const fullUrl = `${API_BASE_URL}${normalizedUrl}`;

  // Log non-GET requests to debug payloads
  if (rest.method && rest.method !== "GET") {
    try {
      console.log(`API Request (${normalizedUrl}):`, {
        method: rest.method,
        body: rest.body ? JSON.parse(rest.body) : null,
      });
    } catch (e) {
      console.log(`API Request (${normalizedUrl}):`, {
        method: rest.method,
        body: rest.body,
      });
    }
  }

  try {
    const response = await fetch(fullUrl, {
      headers: {
        "Content-Type": "application/json",
        "x-request-id":
          window.crypto?.randomUUID?.() ||
          Date.now().toString(36) + Math.random().toString(36).substring(2),
        "x-timestamp": Date.now().toString(),
        ...headers,
      },
      ...rest,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // Fallback if not JSON
        const textError = await response.text().catch(() => "");
        if (textError) errorMessage = textError.slice(0, 100);
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error (${normalizedUrl}):`, error);
    throw error;
  }
};

// Banner API functions
export const bannerAPI = {
  // Get all banners (public)
  getPublicBanners: () => apiCall("/banners"),

  // Admin banner operations
  admin: {
    // Get all banners for admin
    getBanners: () =>
      apiCall("/admin/banners", {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Create new banner
    createBanner: (bannerData) =>
      apiCall("/admin/banners", {
        method: "POST",
        body: JSON.stringify(bannerData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Update banner
    updateBanner: (id, bannerData) =>
      apiCall(`/admin/banners/${id}`, {
        method: "PUT",
        body: JSON.stringify(bannerData),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Delete banner
    deleteBanner: (id) =>
      apiCall(`/admin/banners/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),

    // Reorder banners
    reorderBanners: (bannerIds) =>
      apiCall("/admin/banners/reorder", {
        method: "PUT",
        body: JSON.stringify({ bannerIds }),
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }),
  },
};

// Image validation utility
export const validateImageUrl = (url) => {
  const imageRegex = /^https?:\/\/.+/i;
  return imageRegex.test(url);
};

// Form validation utilities
export const validateBannerForm = (data) => {
  const errors = {};

  if (!data.imageUrl?.trim()) {
    errors.imageUrl = "Image URL is required";
  } else if (!validateImageUrl(data.imageUrl)) {
    errors.imageUrl = "Please provide a valid image URL";
  }

  if (!data.altText?.trim()) {
    errors.altText = "Alt text is required";
  } else if (data.altText.length > 100) {
    errors.altText = "Alt text must be less than 100 characters";
  }

  if (!data.link?.trim()) {
    errors.link = "Link URL is required";
  }

  if (data.title && data.title.length > 50) {
    errors.title = "Title must be less than 50 characters";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Error handling utility
export const handleApiError = (error, defaultMessage = "An error occurred") => {
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

// Success notification utility
export const showSuccessMessage = (message) => {
  // You can integrate with your notification system here
  console.log("Success:", message);
  // Example: toast.success(message);
};

// Loading state management
export const createLoadingState = () => {
  let loadingStates = {};

  return {
    setLoading: (key, isLoading) => {
      loadingStates[key] = isLoading;
    },

    isLoading: (key) => loadingStates[key] || false,

    clearAll: () => {
      loadingStates = {};
    },
  };
};
// Order API functions
export const orderAPI = {
  /**
   * Create a new order
   * @param {Object} orderData - Order details
   * @param {string} orderData.gameId - ID of the game
   * @param {string} orderData.packId - ID of the pack
   * @param {string} orderData.accountId - User's game account ID
   * @param {string} orderData.paymentMethod - Payment method (razorpay/matrixsols)
   * @returns {Promise<Object>} Order details
   */
  createOrder: (orderData) =>
    apiCall("/orders", {
      method: "POST",
      body: JSON.stringify(orderData),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }),

  /**
   * Get order by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  getOrderById: (orderId) =>
    apiCall(`/orders/${orderId}`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }),

  /**
   * Get user's orders
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @returns {Promise<Object>} Paginated list of orders
   */
  getMyOrders: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/orders/me?${query}`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
  },

  /**
   * Cancel an order
   * @param {string} orderId - Order ID to cancel
   * @returns {Promise<Object>} Cancellation result
   */
  cancelOrder: (orderId) =>
    apiCall(`/orders/${orderId}/cancel`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }),

  /**
   * Verify Razorpay payment
   * @param {Object} paymentData - Payment verification details
   * @param {string} paymentData.razorpay_order_id - Razorpay order ID
   * @param {string} paymentData.razorpay_payment_id - Razorpay payment ID
   * @param {string} paymentData.razorpay_signature - Razorpay signature
   * @returns {Promise<Object>} Verification result
   */
  verifyRazorpayPayment: (paymentData) =>
    apiCall("/razorpay/verify-payment", {
      method: "POST",
      body: JSON.stringify(paymentData),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }),
};

// src/utils/api.js

const BASE = import.meta.env.VITE_API_URL || ""; // e.g. http://localhost:3000/api
const API = axios.create({
  baseURL: BASE,
  withCredentials: true, // if you rely on cookies; otherwise false
  timeout: 15000,
});

// ensure auth token is applied automatically
API.interceptors.request.use(
  (cfg) => {
    const token = getAccessToken();
    if (token) {
      cfg.headers = {
        ...(cfg.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }

    // Mandatory Security Headers for Anti-Replay
    cfg.headers["x-request-id"] =
      window.crypto?.randomUUID?.() ||
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    cfg.headers["x-timestamp"] = Date.now().toString();

    return cfg;
  },
  (e) => Promise.reject(e),
);

export default API;
